import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useData } from '../context/DataContext';
import { CirclePlus as PlusCircle, Pencil, Trash2, Music, Upload, Save, X, CircleAlert as AlertCircle, FileText, FileMusic, Image, FileAudio, RotateCcw, Volume2, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Song } from '../services/AdminService';

export const AdminSongs: React.FC = () => {
  const navigate = useNavigate();
  const { adminService } = useData();
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSong, setEditingSong] = useState<Partial<Song> | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [lrcFile, setLrcFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [vocalFile, setVocalFile] = useState<File | null>(null);
  const [midiFile, setMidiFile] = useState<File | null>(null);
  const [fullMixFile, setFullMixFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [error, setError] = useState<string | null>(null);
  const [songToDelete, setSongToDelete] = useState<Song | null>(null);
  const [songToRestore, setSongToRestore] = useState<Song | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [existingFiles, setExistingFiles] = useState<{
    audio: boolean;
    cover: boolean;
    lyrics: boolean;
    vocals: boolean;
    midi: boolean;
    fullMix: boolean;
  }>({
    audio: false,
    cover: false,
    lyrics: false,
    vocals: false,
    midi: false,
    fullMix: false
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const [measuringAll, setMeasuringAll] = useState(false);
  const [measureQueue, setMeasureQueue] = useState<Song[]>([]);
  const [measureCurrent, setMeasureCurrent] = useState<Song | null>(null);
  const [measureProgress, setMeasureProgress] = useState({ done: 0, total: 0 });
  const [measuringSingleId, setMeasuringSingleId] = useState<string | null>(null);
  const measureAbortRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const difficulties = ['Easy', 'Medium', 'Hard', 'Expert'];
  const genres = ['Pop', 'Rock', 'R&B', 'Hip Hop', 'Country', 'Latin', 'Electronic', 'Jazz', 'Classical'];

  useEffect(() => {
    loadSongs();
  }, [showDeleted]);

  const loadSongs = async () => {
    try {
      const songs = await adminService.getAllSongs(showDeleted);
      setSongs(songs);
    } catch (error) {
      console.error('Error loading songs:', error);
      setError('Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  // Poll DB every 30s to refresh LUFS values while batch measurement is running
  useEffect(() => {
    if (measuringAll) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const refreshed = await adminService.getAllSongs(showDeleted);
          setSongs(refreshed);
        } catch { /* ignore poll errors */ }
      }, 30000);
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [measuringAll]);

  const measureSingleSong = async (song: Song) => {
    setMeasuringSingleId(song.id);
    try {
      const result = await adminService.measureTrackLoudness(song.id);
      setSongs(prev => prev.map(s =>
        s.id === song.id ? { ...s, backing_track_lufs: result.backing_track_lufs } : s
      ));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Loudness measurement failed');
    } finally {
      setMeasuringSingleId(null);
    }
  };

  const startMeasureAll = useCallback(async () => {
    const songsWithAudio = songs.filter(s => s.audio_url && !s.is_deleted);
    if (songsWithAudio.length === 0) return;

    measureAbortRef.current = false;
    setMeasuringAll(true);
    setMeasureQueue(songsWithAudio);
    setMeasureProgress({ done: 0, total: songsWithAudio.length });

    for (let i = 0; i < songsWithAudio.length; i++) {
      if (measureAbortRef.current) break;
      const song = songsWithAudio[i];
      setMeasureCurrent(song);
      setMeasureProgress({ done: i, total: songsWithAudio.length });

      try {
        const result = await adminService.measureTrackLoudness(song.id);
        setSongs(prev => prev.map(s =>
          s.id === song.id ? { ...s, backing_track_lufs: result.backing_track_lufs } : s
        ));
      } catch (e) {
        console.error(`Failed to measure song ${song.title}:`, e);
      }
    }

    setMeasureProgress(prev => ({ ...prev, done: prev.total }));
    setMeasureCurrent(null);
    setMeasuringAll(false);
    setMeasureQueue([]);

    // Final refresh
    try {
      const refreshed = await adminService.getAllSongs(showDeleted);
      setSongs(refreshed);
    } catch { /* ignore */ }
  }, [songs, showDeleted, adminService]);

  const stopMeasureAll = () => {
    measureAbortRef.current = true;
  };

  useEffect(() => {
    if (editingSong && editingSong.id) {
      setExistingFiles({
        audio: !!editingSong.audio_url,
        cover: !!editingSong.cover_url,
        lyrics: !!editingSong.lyrics_url,
        vocals: !!editingSong.lead_vocal_url,
        midi: !!editingSong.midi_url,
        fullMix: !!editingSong.full_mix_url
      });
    } else {
      setExistingFiles({
        audio: false,
        cover: false,
        lyrics: false,
        vocals: false,
        midi: false,
        fullMix: false
      });
    }
  }, [editingSong]);

  const uploadFile = async (file: File, path: string): Promise<string> => {
    try {
      const fileType = path.split('/')[0];
      setUploadProgress(prev => ({ ...prev, [fileType]: 0 }));
      setIsUploading(true);
      
      const interval = setInterval(() => {
        setUploadProgress(prev => ({
          ...prev,
          [fileType]: Math.min((prev[fileType] || 0) + 10, 90)
        }));
      }, 200);
      
      const publicUrl = await adminService.uploadFile(file, path);
      
      clearInterval(interval);
      setUploadProgress(prev => ({ ...prev, [fileType]: 100 }));
      
      return publicUrl;
    } catch (error) {
      console.error(`Error uploading ${path}:`, error);
      setUploadProgress(prev => ({ ...prev, [path.split('/')[0]]: 0 }));
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editingSong?.title || !editingSong?.artist) {
      setError('Title and artist are required');
      return;
    }

    if (!audioFile && !existingFiles.audio && !editingSong.audio_url) {
      setError('Record track file is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      let audioUrl = editingSong.audio_url;
      let coverUrl = editingSong.cover_url;
      let lyricsUrl = editingSong.lyrics_url;
      let vocalUrl = editingSong.lead_vocal_url;
      let midiUrl = editingSong.midi_url;
      let fullMixUrl = editingSong.full_mix_url;

      const uploadPromises = [];
      
      if (audioFile) {
        const fileName = `${Date.now()}-${audioFile.name}`;
        uploadPromises.push(
          uploadFile(audioFile, `audio/${fileName}`)
            .then(url => { audioUrl = url; })
        );
      }

      if (coverFile) {
        const fileName = `${Date.now()}-${coverFile.name}`;
        uploadPromises.push(
          uploadFile(coverFile, `covers/${fileName}`)
            .then(url => { coverUrl = url; })
        );
      }

      if (lrcFile) {
        const fileName = `${Date.now()}-${lrcFile.name}`;
        uploadPromises.push(
          uploadFile(lrcFile, `lyrics/${fileName}`)
            .then(url => { lyricsUrl = url; })
        );
      }

      if (vocalFile) {
        const fileName = `${Date.now()}-${vocalFile.name}`;
        uploadPromises.push(
          uploadFile(vocalFile, `vocals/${fileName}`)
            .then(url => { vocalUrl = url; })
        );
      }

      if (midiFile) {
        const fileName = `${Date.now()}-${midiFile.name}`;
        uploadPromises.push(
          uploadFile(midiFile, `midi/${fileName}`)
            .then(url => { midiUrl = url; })
        );
      }

      if (fullMixFile) {
        const fileName = `${Date.now()}-${fullMixFile.name}`;
        uploadPromises.push(
          uploadFile(fullMixFile, `full-mix/${fileName}`)
            .then(url => { fullMixUrl = url; })
        );
      }

      await Promise.all(uploadPromises);

      const { slug, ...songDataWithoutSlug } = editingSong;

      const songData = {
        ...songDataWithoutSlug,
        audio_url: audioUrl,
        cover_url: coverUrl,
        lyrics_url: lyricsUrl,
        lead_vocal_url: vocalUrl,
        midi_url: midiUrl,
        full_mix_url: fullMixUrl
      };

      if (editingSong.id) {
        await adminService.updateSong(editingSong.id, songData);
      } else {
        if (!audioUrl) {
          throw new Error('Record track file is required for new songs');
        }
        await adminService.createSong(songData);
      }

      await loadSongs();
      setEditingSong(null);
      setAudioFile(null);
      setLrcFile(null);
      setCoverFile(null);
      setVocalFile(null);
      setMidiFile(null);
      setFullMixFile(null);
      setUploadProgress({});
      setExistingFiles({
        audio: false,
        cover: false,
        lyrics: false,
        vocals: false,
        midi: false,
        fullMix: false
      });
    } catch (error) {
      console.error('Error saving song:', error);
      setError('Failed to save song. Please ensure all files are uploaded correctly.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      console.log('AdminSongs: Starting delete process for song:', songToDelete);
      setLoading(true);
      setError(null);

      if (!songToDelete?.id) {
        console.error('AdminSongs: No song ID provided for deletion');
        throw new Error('Invalid song ID');
      }

      console.log('AdminSongs: Calling adminService.deleteSong with ID:', songToDelete.id);
      await adminService.deleteSong(songToDelete.id);
      console.log('AdminSongs: Delete operation completed successfully');

      await loadSongs();
      console.log('AdminSongs: Songs reloaded successfully');

      setSongToDelete(null);
    } catch (error) {
      console.error('AdminSongs: Error in handleDelete:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete song');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    try {
      console.log('AdminSongs: Starting restore process for song:', songToRestore);
      setLoading(true);
      setError(null);

      if (!songToRestore?.id) {
        console.error('AdminSongs: No song ID provided for restoration');
        throw new Error('Invalid song ID');
      }

      console.log('AdminSongs: Calling adminService.restoreSong with ID:', songToRestore.id);
      await adminService.restoreSong(songToRestore.id);
      console.log('AdminSongs: Restore operation completed successfully');

      await loadSongs();
      console.log('AdminSongs: Songs reloaded successfully');

      setSongToRestore(null);
    } catch (error) {
      console.error('AdminSongs: Error in handleRestore:', error);
      setError(error instanceof Error ? error.message : 'Failed to restore song');
    } finally {
      setLoading(false);
    }
  };

  const getFileNameFromUrl = (url: string | undefined): string => {
    if (!url) return 'No file selected';
    
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    
    try {
      return decodeURIComponent(fileName);
    } catch (e) {
      return fileName;
    }
  };

  const handleRemoveFile = (fileType: 'audio' | 'cover' | 'lyrics' | 'vocals' | 'midi' | 'fullMix') => {
    if (!editingSong) return;

    let urlKey: keyof Song;
    if (fileType === 'vocals') {
      urlKey = 'lead_vocal_url';
    } else if (fileType === 'midi') {
      urlKey = 'midi_url';
    } else if (fileType === 'fullMix') {
      urlKey = 'full_mix_url';
    } else {
      urlKey = `${fileType}_url` as keyof Song;
    }

    setEditingSong({
      ...editingSong,
      [urlKey]: null
    });
    setExistingFiles(prev => ({
      ...prev,
      [fileType]: false
    }));
  };

  const isAnyFileUploading = Object.values(uploadProgress).some(progress => progress > 0 && progress < 100) || isUploading;

  return (
    <div className="container mx-auto p-6">
      {/* Loudness measurement progress banner */}
      {measuringAll && (
        <div className="mb-6 bg-sky-900/30 border border-sky-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Loader className="w-5 h-5 text-sky-400 animate-spin" />
              <span className="text-white font-medium">
                Measuring loudness: {measureProgress.done} / {measureProgress.total}
              </span>
            </div>
            <button
              onClick={stopMeasureAll}
              className="px-3 py-1 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Stop
            </button>
          </div>
          {measureCurrent && (
            <div className="text-sm text-sky-300 mt-1">
              Processing: {measureCurrent.title} - {measureCurrent.artist}
            </div>
          )}
          {measureQueue.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-slate-400 mb-1">Queue:</div>
              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                {measureQueue.slice(measureProgress.done + 1, measureProgress.done + 6).map(s => (
                  <span key={s.id} className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded">
                    {s.title}
                  </span>
                ))}
                {measureQueue.length - measureProgress.done - 1 > 5 && (
                  <span className="text-xs text-slate-500">
                    +{measureQueue.length - measureProgress.done - 6} more
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="mt-3 w-full bg-slate-700 rounded-full h-1.5">
            <div
              className="bg-sky-500 h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${measureProgress.total > 0 ? (measureProgress.done / measureProgress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Song Management</h1>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="w-4 h-4 rounded border-white/20 bg-white/10 text-kod-purple-500 focus:ring-2 focus:ring-kod-purple-500 cursor-pointer"
            />
            <span className="text-white/70">Show Deleted</span>
          </label>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={startMeasureAll}
            disabled={measuringAll}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Volume2 size={16} />
            {measuringAll ? 'Measuring...' : 'Measure All Loudness'}
          </button>
          <button
            onClick={() => setEditingSong({})}
            className="btn-primary flex items-center"
          >
          <PlusCircle size={18} className="mr-2" />
          Add New Song
        </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {songToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-6">
              <div className="bg-red-500/10 p-3 rounded-full mr-4">
                <AlertCircle className="text-red-500" size={24} />
              </div>
              <h2 className="text-xl font-bold">Delete Song</h2>
            </div>

            <p className="text-white/70 mb-6">
              Are you sure you want to delete "{songToDelete.title}" by {songToDelete.artist}?
              The song will be hidden from song lists but will remain accessible for playback of existing recordings.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setSongToDelete(null)}
                className="btn-outline"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                ) : (
                  <Trash2 size={18} className="mr-2" />
                )}
                Delete Song
              </button>
            </div>
          </div>
        </div>
      )}

      {songToRestore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center mb-6">
              <div className="bg-green-500/10 p-3 rounded-full mr-4">
                <RotateCcw className="text-green-500" size={24} />
              </div>
              <h2 className="text-xl font-bold">Restore Song</h2>
            </div>

            <p className="text-white/70 mb-6">
              Are you sure you want to restore "{songToRestore.title}" by {songToRestore.artist}?
              The song will become available again for practice and recording.
            </p>

            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setSongToRestore(null)}
                className="btn-outline"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition-colors flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                ) : (
                  <RotateCcw size={18} className="mr-2" />
                )}
                Restore Song
              </button>
            </div>
          </div>
        </div>
      )}

      {editingSong && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-2xl w-full my-8 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-6 flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingSong.id ? 'Edit Song' : 'Add New Song'}
              </h2>
              <button
                onClick={() => setEditingSong(null)}
                className="text-white/70 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 px-1">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  value={editingSong.title || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, title: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Artist</label>
                <input
                  type="text"
                  value={editingSong.artist || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, artist: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Genre</label>
                <select
                  value={editingSong.genre || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, genre: e.target.value })}
                  className="input"
                >
                  <option value="">Select Genre</option>
                  {genres.map((genre) => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Release Year</label>
                <input
                  type="number"
                  value={editingSong.release_year || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, release_year: parseInt(e.target.value) })}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Difficulty</label>
                <select
                  value={editingSong.difficulty || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, difficulty: e.target.value })}
                  className="input"
                >
                  <option value="">Select Difficulty</option>
                  {difficulties.map((diff) => (
                    <option key={diff} value={diff}>{diff}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Duration (seconds)</label>
                <input
                  type="number"
                  value={editingSong.duration || ''}
                  onChange={(e) => setEditingSong({ ...editingSong, duration: parseInt(e.target.value) })}
                  className="input"
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingSong.is_audition || false}
                    onChange={(e) => setEditingSong({ ...editingSong, is_audition: e.target.checked })}
                    className="form-checkbox h-5 w-5 text-kod-accent rounded border-kod-purple-600 bg-kod-purple-700 focus:ring-2 focus:ring-kod-accent"
                  />
                  <span className="text-sm font-medium">Audition Song</span>
                </label>
                <p className="text-xs text-white/50 mt-1">Mark this song as available for auditions</p>
              </div>

              <div className="col-span-2 border-t border-white/10 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-white/80 mb-3">Channel Availability</h4>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingSong.hide_from_kiosk || false}
                      onChange={(e) => setEditingSong({ ...editingSong, hide_from_kiosk: e.target.checked })}
                      className="form-checkbox h-5 w-5 text-red-400 rounded border-kod-purple-600 bg-kod-purple-700 focus:ring-2 focus:ring-red-400"
                    />
                    <span className="text-sm font-medium">Hide from Kiosk</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingSong.hide_from_singalong || false}
                      onChange={(e) => setEditingSong({ ...editingSong, hide_from_singalong: e.target.checked })}
                      className="form-checkbox h-5 w-5 text-red-400 rounded border-kod-purple-600 bg-kod-purple-700 focus:ring-2 focus:ring-red-400"
                    />
                    <span className="text-sm font-medium">Hide from Sing Along</span>
                  </label>
                </div>
                <p className="text-xs text-white/50 mt-2">Hidden songs won't appear when building new Kiosk leagues or Sing Along bundles</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Record Track (Vocals-only backing track for recording)</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.audio && editingSong.audio_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <FileMusic size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.audio_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('audio')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove record track file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.audio || !editingSong.audio_url) && (
                    <input
                      type="file"
                      accept=".mp3"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Vocals Only File (MP3)</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.vocals && editingSong.lead_vocal_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <FileMusic size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.lead_vocal_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('vocals')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove vocals file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.vocals || !editingSong.lead_vocal_url) && (
                    <input
                      type="file"
                      accept=".mp3"
                      onChange={(e) => setVocalFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Lyrics File (LRC)</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.lyrics && editingSong.lyrics_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <FileText size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.lyrics_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('lyrics')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove lyrics file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.lyrics || !editingSong.lyrics_url) && (
                    <input
                      type="file"
                      accept=".lrc"
                      onChange={(e) => setLrcFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Cover Image</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.cover && editingSong.cover_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <Image size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.cover_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('cover')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove cover image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.cover || !editingSong.cover_url) && (
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">MIDI File (Optional)</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.midi && editingSong.midi_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <FileAudio size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.midi_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('midi')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove MIDI file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.midi || !editingSong.midi_url) && (
                    <input
                      type="file"
                      accept=".mid,.midi"
                      onChange={(e) => setMidiFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Practice Track (Full song with vocals for practice mode)</label>
                <div className="flex flex-col space-y-2">
                  {existingFiles.fullMix && editingSong.full_mix_url && (
                    <div className="bg-kod-purple-700 p-2 rounded-md flex items-center justify-between">
                      <div className="flex items-center">
                        <FileMusic size={16} className="text-kod-accent mr-2" />
                        <span className="text-sm truncate max-w-md">{getFileNameFromUrl(editingSong.full_mix_url)}</span>
                      </div>
                      <button
                        onClick={() => handleRemoveFile('fullMix')}
                        className="p-1 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded"
                        title="Remove practice track file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                  {(!existingFiles.fullMix || !editingSong.full_mix_url) && (
                    <input
                      type="file"
                      accept=".mp3,.wav,.flac"
                      onChange={(e) => setFullMixFile(e.target.files?.[0] || null)}
                      className="input"
                    />
                  )}
                </div>
              </div>
            </div>

            {Object.entries(uploadProgress).map(([type, progress]) => (
              progress > 0 && (
                <div key={type} className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize">{type} Upload</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-kod-purple-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-kod-accent rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )
            ))}

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setEditingSong(null)}
                className="btn-outline"
                disabled={isSaving || isAnyFileUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className={`btn-primary flex items-center ${(isSaving || isAnyFileUploading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isSaving || isAnyFileUploading}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Save Song
                  </>
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-kod-purple-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-kod-purple-700">
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Artist</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Genre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Difficulty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">LUFS</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Channels</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kod-purple-700">
              {songs.map((song) => (
                <tr key={song.id} className={`hover:bg-kod-purple-700/50 ${song.is_deleted ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {song.title}
                    {song.is_deleted && <span className="ml-2 text-xs text-red-400">(Deleted)</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{song.artist}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{song.genre}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      song.difficulty === 'Easy' ? 'bg-green-900/30 text-green-300' :
                      song.difficulty === 'Medium' ? 'bg-blue-900/30 text-blue-300' :
                      song.difficulty === 'Hard' ? 'bg-orange-900/30 text-orange-300' :
                      'bg-red-900/30 text-red-300'
                    }`}>
                      {song.difficulty}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {song.backing_track_lufs != null ? (
                        <span className="text-sm text-emerald-400 font-mono">
                          {Number(song.backing_track_lufs).toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-xs text-white/40">--</span>
                      )}
                      {measuringSingleId === song.id ? (
                        <Loader className="w-4 h-4 text-sky-400 animate-spin" />
                      ) : (
                        <button
                          onClick={() => measureSingleSong(song)}
                          disabled={!song.audio_url || measuringAll}
                          className="p-1 text-white/40 hover:text-sky-400 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
                          title={song.backing_track_lufs != null ? 'Re-measure loudness' : 'Measure loudness'}
                        >
                          <Volume2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {song.is_deleted ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-300">
                        Deleted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-300">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      {song.hide_from_kiosk && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/30 text-amber-300">
                          No Kiosk
                        </span>
                      )}
                      {song.hide_from_singalong && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/30 text-amber-300">
                          No SA
                        </span>
                      )}
                      {!song.hide_from_kiosk && !song.hide_from_singalong && (
                        <span className="text-xs text-white/30">All</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingSong(song)}
                        className="p-2 text-white/70 hover:text-white hover:bg-kod-purple-600 rounded-lg"
                        title="Edit song"
                      >
                        <Pencil size={18} />
                      </button>
                      {song.is_deleted ? (
                        <button
                          onClick={() => setSongToRestore(song)}
                          className="p-2 text-white/70 hover:text-green-500 hover:bg-green-500/10 rounded-lg"
                          title="Restore song"
                        >
                          <RotateCcw size={18} />
                        </button>
                      ) : (
                        <button
                          onClick={() => setSongToDelete(song)}
                          className="p-2 text-white/70 hover:text-red-500 hover:bg-red-500/10 rounded-lg"
                          title="Delete song"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};